/**
 * Recent documents and Favorites for the iManage area. Both are kept in this
 * browser only (localStorage, per organization): iManage has its own
 * recent and favorites lists, which this client does not read, so these are
 * Amoreg's lists of what was opened and starred here, not iManage's.
 *
 * Entries hold only what a row needs to render and reopen (IDs, name, kind)
 * — never document content.
 *
 * @module components/apps/imanage/imanageLocalLists
 */
import type { IManageEntry } from "@amoreg/contracts/imanage";
import { useCallback, useSyncExternalStore } from "react";

import { documentKey } from "./imanagePaths";

export interface IManageRef {
  readonly id: string;
  readonly name: string;
  readonly kind: IManageEntry["kind"];
  readonly library: string;
  readonly extension?: string;
  readonly email?: boolean;
  readonly version?: number | null;
  readonly author?: string;
  readonly modified?: string;
  readonly size?: number;
  readonly workspaceId?: string;
  readonly folderId?: string;
  readonly client?: string;
  readonly matter?: string;
  /** Epoch ms it was opened or starred. */
  readonly at: number;
}

export type IManageListName = "recent" | "favorites";
export const RECENT_LIMIT = 30;
export const FAVORITES_LIMIT = 200;

/** Identity across versions: a document is one favorite, one recent row. */
export const refKey = (ref: Pick<IManageRef, "id" | "kind">) =>
  ref.kind === "document" ? documentKey(ref.id) : ref.id;

export function refFromEntry(
  entry: IManageEntry,
  context: { library: string; folderId?: string | null; workspaceId?: string | null },
  now: number,
): IManageRef {
  const ref: { -readonly [K in keyof IManageRef]?: IManageRef[K] } = {
    id: entry.id,
    name: entry.name,
    kind: entry.kind,
    library: context.library,
    at: now,
  };
  if (entry.extension) ref.extension = entry.extension;
  if (entry.email) ref.email = true;
  if (entry.version !== null) ref.version = entry.version;
  if (entry.author) ref.author = entry.author;
  if (entry.modified) ref.modified = entry.modified;
  if (entry.size !== undefined) ref.size = entry.size;
  const workspaceId = entry.workspaceId ?? context.workspaceId;
  if (workspaceId) ref.workspaceId = workspaceId;
  if (context.folderId) ref.folderId = context.folderId;
  if (entry.client) ref.client = entry.client;
  if (entry.matter) ref.matter = entry.matter;
  return ref as IManageRef;
}

/** A stored Recent/Favorites row as a table entry. */
export function entryFromRef(ref: IManageRef): IManageEntry {
  const entry: { -readonly [K in keyof IManageEntry]?: IManageEntry[K] } = {
    id: ref.id,
    name: ref.name,
    kind: ref.kind,
    version: ref.version ?? null,
  };
  if (ref.extension) entry.extension = ref.extension;
  if (ref.email) entry.email = true;
  if (ref.author) entry.author = ref.author;
  if (ref.modified) entry.modified = ref.modified;
  if (ref.size !== undefined) entry.size = ref.size;
  if (ref.workspaceId) entry.workspaceId = ref.workspaceId;
  if (ref.client) entry.client = ref.client;
  if (ref.matter) entry.matter = ref.matter;
  return entry as IManageEntry;
}

/** Most recent first, one row per document, bounded. */
export function withRecent(
  list: readonly IManageRef[],
  ref: IManageRef,
  limit = RECENT_LIMIT,
): IManageRef[] {
  const key = refKey(ref);
  return [ref, ...list.filter((x) => refKey(x) !== key)].slice(0, limit);
}

/** Adds the item when absent, removes it when present. */
export function toggledFavorite(
  list: readonly IManageRef[],
  ref: IManageRef,
  limit = FAVORITES_LIMIT,
): IManageRef[] {
  const key = refKey(ref);
  return list.some((x) => refKey(x) === key)
    ? list.filter((x) => refKey(x) !== key)
    : [ref, ...list].slice(0, limit);
}

/** Tolerates absent, foreign or corrupt storage: anything unreadable is an empty list. */
export function parseList(raw: string | null): IManageRef[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter(
      (x): x is IManageRef =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as IManageRef).id === "string" &&
        typeof (x as IManageRef).name === "string" &&
        typeof (x as IManageRef).library === "string" &&
        ["library", "matter", "folder", "document"].includes((x as IManageRef).kind) &&
        typeof (x as IManageRef).at === "number",
    );
  } catch {
    return [];
  }
}

export const storageKey = (organizationId: string, list: IManageListName) =>
  `amoreg:imanage:${list}:v1:${organizationId}`;

const listeners = new Set<() => void>();
const snapshots = new Map<string, { raw: string | null; value: IManageRef[] }>();

function read(key: string): IManageRef[] {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    raw = null;
  }
  const cached = snapshots.get(key);
  if (cached && cached.raw === raw) return cached.value;
  const value = parseList(raw);
  snapshots.set(key, { raw, value });
  return value;
}

function write(key: string, value: readonly IManageRef[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the list simply does not persist.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key?.startsWith("amoreg:imanage:")) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

const EMPTY: IManageRef[] = [];

export function useIManageList(organizationId: string | null, list: IManageListName) {
  const key = organizationId ? storageKey(organizationId, list) : null;
  const items = useSyncExternalStore(
    subscribe,
    () => (key ? read(key) : EMPTY),
    () => EMPTY,
  );
  const update = useCallback(
    (change: (current: readonly IManageRef[]) => IManageRef[]) => {
      if (key) write(key, change(read(key)));
    },
    [key],
  );
  return [items, update] as const;
}

/** Records an opened document in Recent. */
export function recordRecent(organizationId: string, ref: IManageRef) {
  const key = storageKey(organizationId, "recent");
  write(key, withRecent(read(key), ref));
}

const libraryKey = (organizationId: string) => `amoreg:imanage:library:v1:${organizationId}`;

function readLibrary(organizationId: string): string | null {
  try {
    const value = window.localStorage.getItem(libraryKey(organizationId));
    return value && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * The library the area shows when a page does not name one: the last one
 * chosen here if it is still offered, else the first library offered.
 */
export function resolveLibrary(
  stored: string | null,
  offered: readonly { readonly id: string }[] | null,
): string | null {
  if (!offered) return stored;
  if (stored && offered.some((library) => library.id === stored)) return stored;
  return offered[0]?.id ?? null;
}

export function useLibraryChoice(organizationId: string | null) {
  const stored = useSyncExternalStore(
    subscribe,
    () => (organizationId ? readLibrary(organizationId) : null),
    () => null,
  );
  const choose = useCallback(
    (library: string) => {
      if (!organizationId) return;
      try {
        window.localStorage.setItem(libraryKey(organizationId), library);
      } catch {
        // Not persisted; the choice still applies until reload.
      }
      for (const listener of listeners) listener();
    },
    [organizationId],
  );
  return [stored, choose] as const;
}
